// react-i18next instance (replaces Lingui). Translations are bundled in-repo as
// JSON, nested per namespace (`frontend` / `backend`). Keys are structured
// (`frontend:domain.section.element`), so `nsSeparator` (`:`) and `keySeparator`
// (`.`) are enabled. `fallbackLng: false` => a miss renders the raw key, which
// makes untranslated strings visible; `de-DE` is the complete source language.
// Plurals use i18next's native suffix keys (`key_one` / `key_other`, selected by
// the `count` option) and interpolation uses `{{var}}`. `compatibilityJSON: 'v4'`
// matches the format used by client-canvas. (No ICU plugin.)
import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import deDE from '../locales/de-DE.json'
import enDE from '../locales/en-DE.json'

export const SUPPORTED_LOCALES = ['de-DE', 'en-DE'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]
export const DEFAULT_LOCALE: AppLocale = 'de-DE'

// Read the persisted language defensively. This runs at module load, and
// `localStorage` access can throw (Safari private mode, storage disabled, or
// an opaque-origin jsdom under test) — a throw here would break every importer.
function readSavedLanguage(): AppLocale | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage.getItem('language') as AppLocale | null
  } catch {
    return null
  }
}

const savedLanguage = readSavedLanguage()

void i18next.use(initReactI18next).init({
  compatibilityJSON: 'v4',
  resources: {
    'de-DE': deDE,
    'en-DE': enDE,
  },
  lng:
    savedLanguage && SUPPORTED_LOCALES.includes(savedLanguage)
      ? savedLanguage
      : DEFAULT_LOCALE,
  fallbackLng: false,
  supportedLngs: SUPPORTED_LOCALES,
  defaultNS: 'frontend',
  ns: ['frontend', 'backend'],
  keySeparator: '.',
  nsSeparator: ':',
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
})

export default i18next
